import styles from './index.module.css'

import * as React from 'react'
import { useEffect, useState } from 'react'

import { TRACK_PLAYLISTED_FROM_SIM_TAB } from '@disco/constants/EventTypes'
import { getSimilarTracks } from '@disco/state-management/actions/SearchActions'
import { getTrack } from '@disco/state-management/selectors/tracks'

import type { ParentProps } from 'types/explorer'
import type { Track, TrackId } from 'types/track'

import { useDispatch, useSelector } from 'hooks/use-redux'

import PaneItems from './../pane-items'
import PaneItemsSkeleton from './../pane-items-skeleton'
import SimilarityAlert from './similarity-alert'

type Props = {
  parentProps: ParentProps
  trackId: TrackId
  aggregatedTrackURL?: string
  /** Reports how many similar tracks were found once results resolve, so
   * the parent can surface the count (e.g. in a segment badge). */
  onResultsCount?: (count: number) => void
}
type State = null | {
  err?: string
  results?: Track[] | null | undefined
}
const excludeErrors = ['No embeddings found for the provided audio ID.']

const Similarity = ({
  parentProps,
  trackId,
  aggregatedTrackURL,
  onResultsCount,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const track = useSelector(state =>
    getTrack(state, {
      id: trackId,
    }),
  )
  const [sim, setSim] = useState<State>(null)
  // Kept in a ref so the fetch effect doesn't re-run when the parent passes
  // a new callback identity on re-render.
  const onResultsCountRef = React.useRef(onResultsCount)
  onResultsCountRef.current = onResultsCount
  useEffect(() => {
    ;(async function () {
      try {
        const results = await dispatch(
          getSimilarTracks(trackId, aggregatedTrackURL),
        )
        setSim({
          results,
        })
        onResultsCountRef.current?.(results?.length ?? 0)
      } catch (err) {
        if (!excludeErrors.includes(err.message)) {
          setSim({
            err: err.message,
          })
        } else {
          setSim({
            results: null,
          })
          onResultsCountRef.current?.(0)
        }
      }
    })()
  }, [dispatch, trackId])
  if (!sim) return <PaneItemsSkeleton />
  const tracks =
    sim.results?.map(track => ({
      ...track,
      internal_data: {
        ...track.internal_data,
        [TRACK_PLAYLISTED_FROM_SIM_TAB]: {
          panelId: trackId,
        },
      },
    })) || []

  return (
    <>
      <div className={styles.similarityResult}>
        {track && <SimilarityAlert errorMessage={sim.err} />}

        {sim?.results && (
          <PaneItems
            itemPrefix="similarity"
            items={tracks}
            isSimilarity
            {...parentProps}
          />
        )}
      </div>
    </>
  )
}

export default Similarity
